import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { usePronouncedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import {
  getQuestUnlock,
  type Quest,
  type QuestUnlock,
} from "@-/dev.improve.haniverse.lib.quest.catalog";
import { useUserQuestsQueryOptions } from "@-/dev.improve.haniverse.lib.quest.progress";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { useBrowseLevelsStudyGraphemesQuery } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import { useMemo } from "react";

export interface QuestStatus {
  isCompleted: boolean;
  quest: Quest;
  unlock: QuestUnlock;
}

/**
 * Where the learner stands on each quest: unlocked by what they have learned, and completed.
 * `undefined` until their study and quest data has loaded, so nothing shows as locked by mistake.
 */
export function useQuestStatuses(quests: readonly Quest[]): QuestStatus[] | undefined {
  const { hanziScript } = useActorUser();
  const levels = useLevels();
  const { data: studiedGraphemes } = useBrowseLevelsStudyGraphemesQuery();
  const { data: pronounced } = useQuery(usePronouncedGraphemesQueryOptions());
  const { data: userQuests } = useQuery(useUserQuestsQueryOptions());
  return useMemo(() => {
    if (studiedGraphemes === undefined || pronounced === undefined || userQuests === undefined) {
      return undefined;
    }
    const completedQuestIds = new Set(
      userQuests.filter((row) => row.completedAt !== null).map((row) => row.questId),
    );
    return quests.map((quest) => ({
      isCompleted: completedQuestIds.has(quest.id),
      quest,
      unlock: getQuestUnlock({ hanziScript, levels, pronounced, quest, studiedGraphemes }),
    }));
  }, [hanziScript, levels, pronounced, quests, studiedGraphemes, userQuests]);
}
